import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import StandardReports from '../../StandardReports';
import {
  mergeApiDataIntoConfigForReviewersCount,
  resolveSelectedDailyHourly,
} from '../../common/ReportHelper';
import {Reports, RightPanelConfig} from '../../config/reportTypes';
import {
  useExportReviewerCountsReport,
  useLeftPanelReport,
  useReviewLeftPanel,
  useReviewerCountReport,
  useReviewerCountViewReport,
} from '../../../../../hooks/reporting/useReporting';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import reviewersCountReportJson from '../../config/ReviewersCountReport.json';
import {
  ReviewerAssignmentSummary,
  ReviewerCountViewReportResponse,
} from '../../../../../api/types/reportingApi.type';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {
  PAGE_SIZE,
  REPORT_PAGE_SIZE_OPTIONS,
  ToastType,
} from '../../../../../shared/constants';
import {ToastMessageConfigType} from '../../../../../api/types/ingestionApi.type';
import {t} from 'i18next';
import DocCountSummaryView from '../review-results/component/DocCountSummaryView/DocCountSummaryView';
import dayjs from 'dayjs';
import './ReviewersCountContainer.scss';
import {useLocation} from 'react-router-dom';
import {getReportState} from '../../../../../api/endpoints/reportingApi';
import utc from 'dayjs/plugin/utc';

interface ReportConfig {
  infoContent: string | string[] | undefined;
  title: string;
  showInfoIcon?: boolean;
  config: Reports;
}

const DEFAULT_PAGE_SIZE = 50;

const toSelectedReviewersBooleanArray = (
  selectedReviewers: any,
  reviewerList: any[]
): boolean[] => {
  if (!Array.isArray(selectedReviewers)) {
    return [];
  }

  const isBooleanArray =
    selectedReviewers.length > 0 &&
    selectedReviewers.every((value: any) => typeof value === 'boolean');
  if (isBooleanArray) {
    return selectedReviewers;
  }

  const isIdArray =
    selectedReviewers.length > 0 &&
    selectedReviewers.every(
      (value: any) => typeof value === 'number' || typeof value === 'string'
    );
  if (!isIdArray) {
    return [];
  }

  const selectedIds = new Set(selectedReviewers.map((id: any) => String(id)));
  return reviewerList.map((reviewer: any) =>
    selectedIds.has(String(reviewer?.reviewerID))
  );
};

const ReviewersCountContainer: React.FC = () => {
  const {data: leftPanelData, isPending: isLoadingLeftPanel} =
    useReviewLeftPanel(2);
  const {mutateAsync: runReviewerCount} = useReviewerCountReport();
  const {mutateAsync: runReviewerCountView} = useReviewerCountViewReport();
  const {mutate: exportReviewerCountsReport} = useExportReviewerCountsReport();
  const [reportConfig, setReportConfig] = useState<ReportConfig | null>(null);
  const [dynamicData, setDynamicData] = useState<any>({});
  const [isDailySwitched, setIsDailySwitched] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [apiLoading, setApiLoading] = useState(false);
  const [isLoadingReportState, setIsLoadingReportState] = useState(false);
  const [dailyOrHourly, setDailyOrHourly] = useState<string>('1');
  const [gridData, setGridData] =
    useState<ReviewerCountViewReportResponse | null>(null);
  const [savedFilterValues, setSavedFilterValues] = useState<
    Record<string, any>
  >({});

  const [savedUiFilterValues, setSavedUiFilterValues] = useState<
    Record<string, any>
  >({});

  const [freshStart, setFreshStart] = useState(true);

  const [waitModalInfo, setWaitModalInfo] = useState<{
    notificationID: number;
    reportID: number;
    reportFilePath: string;
    reportName: string;
  } | null>(null);

  const [resetDatePicker, setResetDatePicker] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE);
  const [reviewCountViewData, setReviewCountViewData] =
    useState<ReviewerCountViewReportResponse>();
  const [reviewersColumns, setReviewersColumns] = useState<any[]>([]);
  const [reviewersData, setReviewersData] = useState<any[]>([]);
  const [hiddenColumnIndexes, setHiddenColumnIndexes] = useState<number[]>([]);
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({show: false});
  const [resetCounter, setResetCounter] = useState(0);
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isFromBGtask, setIsFromBGtask] = useState<boolean>(false);
  dayjs.extend(utc);

  const [savedDataTick, setSavedDataTick] = useState(0);
  const [rightPanelColumns, setRightPanelColumns] = useState<any[]>([]);
  const prevColsLenRef = useRef(0);

  const reportIdFromUrl = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      const id = params.get('ReportID');
      const n = id ? Number.parseInt(id, 10) : 0;
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  }, [location.search]);

  const reportLabel = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      const label = params.get('ReportLabel');
      return Boolean(label);
    } catch {
      return false;
    }
  }, [location.search]);

  const {data: leftPanelReportData, isPending: isLoadingLeftPanelReport} =
    useLeftPanelReport(reportIdFromUrl);
  const leftPanelResolvedReport = useMemo(() => {
    return leftPanelReportData ?? [];
  }, [leftPanelReportData]);

  const dataAsOf = useMemo(() => {
    if (
      !Array.isArray(leftPanelResolvedReport) &&
      leftPanelResolvedReport?.dataAsOf
    ) {
      return dayjs
        .utc(leftPanelResolvedReport.dataAsOf)
        .format('YYYY/MM/DD HH:mm:ss');
    } else if (!Array.isArray(gridData) && gridData?.dataAsOf) {
      return dayjs.utc(gridData?.dataAsOf).format('YYYY/MM/DD HH:mm:ss');
    } else {
      return dayjs.utc().format('YYYY/MM/DD HH:mm:ss');
    }
  }, [leftPanelResolvedReport, gridData]);

  const adaptedConfig = useMemo(() => {
    if (!reportConfig?.config) return null as any;
    const cfg = {...reportConfig.config} as any;
    if (Array.isArray(cfg.sections)) {
      cfg.sections = cfg.sections.map((section: any) => ({
        ...section,
        filters: (section.filters ?? []).map((filter: any) => {
          const id = filter?.id;
          let mappedId = id;
          if (id === 'reviewers') mappedId = 'reviewers';
          if (id === 'assignmentGroups') mappedId = 'assignmentGroups';
          if (id === 'dateRangeSection') {
            const isDaily = dailyOrHourly === '1';
            const format = isDaily ? 'YYYY/MM/DD' : 'YYYY/MM/DD HH:mm:ss';

            return {
              ...filter,
              id: mappedId,
              components: (filter.components ?? []).map((component: any) => {
                return {
                  ...component,
                  mode: isDaily ? 'date' : 'date-time',
                  placeHolder: format,
                  initialStartDate: dayjs.utc().format(format),
                  value: component.value
                    ? dayjs(component.value, [
                        'YYYY/MM/DD HH:mm:ss',
                        'YYYY/MM/DD',
                      ]).format(format)
                    : dayjs.utc().format(format),
                };
              }),
            };
          }
          return {...filter, id: mappedId};
        }),
      }));
    }

    if (Array.isArray(cfg.selectionCriteria.fields)) {
      cfg.selectionCriteria.fields.forEach((section: any) => {
        if (section.key === 'selectedDailyHourly') {
          section.value = dailyOrHourly;
        }
      });
    }
    return cfg;
  }, [reportConfig, dailyOrHourly]);

  useEffect(() => {
    setResetDatePicker(true);
    setTimeout(() => {
      setResetDatePicker(false);
    }, 500);
  }, [dailyOrHourly]);

  const initialValuesFromLeftPanel =
    resetCounter > 0 ? [] : leftPanelResolvedReport;

  const initialValuesForMerge = useMemo(() => {
    if (freshStart) return {};
    if (savedUiFilterValues && Object.keys(savedUiFilterValues).length > 0) {
      return savedUiFilterValues;
    }
    return initialValuesFromLeftPanel;
  }, [freshStart, savedUiFilterValues, initialValuesFromLeftPanel]);

  const loadReportConfig = useCallback(async () => {
    const data = (reviewersCountReportJson as any)?.report ?? null;
    setReportConfig(data);
  }, []);

  const loadDynamicData = useCallback(async () => {
    const lp: any = leftPanelData ?? {};
    const reviewers = Array.isArray(lp.reviewerList)
      ? lp.reviewerList.map((r: any) => ({
          reviewer_name: r?.reviewer_Name ?? '',
          actionTypeID: r?.reviewerID ?? 0,
          value: false,
        }))
      : [];
    const assignmentGroups = Array.isArray(lp.lstAssignmentGroup)
      ? lp.lstAssignmentGroup
      : [];
    setDynamicData({reviewers, assignmentGroups});
  }, [leftPanelData]);

  useEffect(() => {
    const run = async () => {
      setSavedFilterValues({});
      setSavedUiFilterValues({});
      setGridData(null);
      setReviewCountViewData(undefined);
      setReviewersData([]);
      setReviewersColumns([]);
      setHiddenColumnIndexes([]);
      setPageNumber(1);
      setResetCounter(0);
      setIsFromBGtask(false);
      setLoading(true);
      await Promise.all([loadReportConfig(), loadDynamicData()]);
      setLoading(false);
      setFreshStart(false);
    };
    run();
  }, [loadReportConfig, loadDynamicData]);

  const buildReviewerLabelMap = useCallback(
    (selectedReviewers: any) => {
      const baseList: any[] = Array.isArray(
        (leftPanelData as any)?.reviewerList
      )
        ? (leftPanelData as any).reviewerList
        : Array.isArray(dynamicData?.reviewers)
          ? dynamicData.reviewers
          : [];

      const getName = (r: any) => r?.reviewer_Name ?? r?.reviewer_name ?? '';
      const getId = (r: any) => r?.reviewerID ?? r?.actionTypeID ?? 0;

      if (!selectedReviewers) return {};

      if (Array.isArray(selectedReviewers)) {
        if (
          baseList.length > 0 &&
          selectedReviewers.length === baseList.length &&
          typeof selectedReviewers[0] === 'boolean'
        ) {
          return baseList.reduce(
            (acc: Record<string, boolean>, r: any, idx: number) => {
              const name = getName(r);
              if (name) acc[name] = Boolean(selectedReviewers[idx]);
              return acc;
            },
            {}
          );
        }

        const idSet = new Set(selectedReviewers.map((x: any) => String(x)));
        return baseList.reduce((acc: Record<string, boolean>, r: any) => {
          const name = getName(r);
          const id = getId(r);
          if (name) acc[name] = idSet.has(String(id));
          return acc;
        }, {});
      }

      if (typeof selectedReviewers === 'object') {
        return selectedReviewers;
      }

      return {};
    },
    [leftPanelData, dynamicData]
  );

  const getSelectedReviewer = (
    filterValues: Record<string, any>,
    savedReportData?: any
  ) => {
    const selectedValues = filterValues.selectedReviewers ?? [];
    const reviewersList = savedReportData?.reviewerList ?? [];
    const selectedReviewerIDs = selectedValues
      .map((isSelected: boolean, index: number) =>
        // eslint-disable-next-line security/detect-object-injection
        isSelected ? reviewersList[index]?.reviewerID : null
      )
      .filter((id: any): id is number => id !== null);

    return selectedReviewerIDs;
  };
  const getSelectedAssignmentIDs = useCallback(
    (values: Record<string, any>) => {
      const rawAssignments = values?.assignmentGroups ?? [];
      let selectedAssignmentGroupIds: number[] = [];
      if (Array.isArray(rawAssignments)) {
        selectedAssignmentGroupIds = rawAssignments
          .map((v: any) => Number(v))
          .filter((n: number) => Number.isFinite(n));
      } else if (rawAssignments && typeof rawAssignments === 'object') {
        selectedAssignmentGroupIds = Object.entries(rawAssignments)
          .filter(([, checked]) => Boolean(checked))
          .map(([key]) => Number(key))
          .filter((n: number) => Number.isFinite(n));
      }

      const allAssignmentIds: string[] = (() => {
        const src: any[] = Array.isArray(
          (leftPanelData as any)?.lstAssignmentGroup
        )
          ? (leftPanelData as any).lstAssignmentGroup
          : [];
        return src.map((n: any) => String(n?.id ?? '')).filter(Boolean);
      })();

      const sel = selectedAssignmentGroupIds.map(String);
      if (sel.length > 0 && sel.length === allAssignmentIds.length) {
        return [...allAssignmentIds, '-1'];
      }
      return sel;
    },
    [leftPanelData]
  );

  const formatMinTwoDecimals = (value: any) => {
    if (value === null || value === undefined) return 'N/A';

    const str = String(value).trim();

    const [intPart, decPart = ''] = str.split('.');

    if (decPart.length === 0) {
      return `${intPart}.00`;
    } else if (decPart.length === 1) {
      return `${intPart}.${decPart}0`;
    } else {
      return `${intPart}.${decPart}`;
    }
  };

  const docCountSummaryView = useMemo(() => {
    const summary = gridData?.reviewerCountReportResponse[0]?.reviewerSummary;
    return (
      <DocCountSummaryView
        leftItems={[
          {
            label: t('reviewerCounts.summary.totalDocs'),
            value: String(summary?.assignedDocs ?? 'N/A'),
          },
          {
            label: t('reviewerCounts.summary.totalReviewedDocs'),
            value: String(summary?.totalReviewedDocs ?? 'N/A'),
          },
          {
            label:
              dailyOrHourly === '1'
                ? t('reviewerCounts.summary.dailyReviewRate')
                : t('reviewerCounts.summary.hourlyReviewRate'),
            value:
              formatMinTwoDecimals(summary?.dailyorHourlyReviewedDocuments) ??
              'N/A',
          },
        ]}
        rightItems={[
          {
            label: t('reviewerCounts.summary.totalDocsReviewedToDate'),
            value: String(summary?.docsReviewedToDate) ?? 'N/A',
          },
          {
            label:
              dailyOrHourly === '1'
                ? t('reviewerCounts.summary.dailyReviewRateToDate')
                : t('reviewerCounts.summary.hourlyReviewRateToDate'),
            value:
              formatMinTwoDecimals(summary?.dailyorHourlyReviewedAvg) ?? 'N/A',
          },
        ]}
      />
    );
  }, [gridData]);

  const buildPayload = (values: Record<string, any>) => {
    const selectedReviewerIDs = getSelectedReviewer(values, leftPanelData);
    const selectedAssignments = getSelectedAssignmentIDs(values);
    const fromDate = Array.isArray(values.fromDate)
      ? values.fromDate.length
        ? values.fromDate
        : ''
      : (values.fromDate ?? '');
    const toDate = Array.isArray(values.toDate)
      ? values.toDate.length
        ? values.toDate
        : ''
      : (values.toDate ?? '');

    const payload: any = {
      include_Assignment_Detail: 1,
      reportID: 0,
      documentID: 0,
      selected_SearchID: [],
      selected_ActionTypeID: [],
      saveSearchID: 0,
      reportTypeCode: 'RCR003',
      showDailyReport: values?.selectedDailyHourly === '1',
      fromDateTime: fromDate ?? '',
      toDateTime: toDate ?? '',
      timeOffset: 0,
      selectedReviewers: selectedReviewerIDs.map(String),
      selectedAssignments,
      selectedCustodians: [],
      selectedFoldersOrGroups: [],
      selectedAssignmentClassification: [],
      selectedTagClassification: [],
      selectedTags: [],
      searchTermReportType: '',
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      isAscending: 'ASC',
      sortingColumn: 'reviewer_Name',
      rowNumber: 0,
      assignmentFlag: values?.assignmentGroups.length > 0,
      selected_UserID: [],
      selectedSecurityGroups: [],
      selectedDocumentTypes: [],
      selectedRedactionTags: [],
      selectedCommentTypes: [],
      selectedProductions: [],
    };
    return payload;
  };

  const isExportingRef = useRef(false);

  const handleExportClick = useCallback(() => {
    if (isExportingRef.current) {
      return;
    }
    const hasApplied =
      savedFilterValues && Object.keys(savedFilterValues).length > 0;
    if (!hasApplied) return;
    isExportingRef.current = true;
    const selectionCriteria: any = buildPayload(savedFilterValues);
    selectionCriteria.selectedCustomColumns = hiddenColumnIndexes;
    exportReviewerCountsReport(
      {
        selectionCriteria: {
          ...selectionCriteria,
          dataAsOf: dayjs
            .utc(dataAsOf, 'YYYY/MM/DD HH:mm:ss', true)
            .format('YYYY-MM-DDTHH:mm:ss'),
        },
        reportType: 'RCR003',
        actionType: 'EXPORTREPORT',
      },
      {
        onSuccess: () => {
          setToastMsgConfig({
            show: true,
            type: ToastType.Success,
            message: t('reports.common.reportExportSuccess'),
          } as any);
        },
        onError: (error: any) => {
          setToastMsgConfig({
            show: true,
            type: ToastType.Error,
            message:
              error?.response?.data?.title ??
              t('reports.common.reportExportError'),
          } as any);
        },
        onSettled: () => {
          isExportingRef.current = false;
        },
      }
    );
  }, [
    exportReviewerCountsReport,
    savedFilterValues,
    hiddenColumnIndexes,
    dataAsOf,
  ]);

  const RIGHT_PANEL_HEADER_CONFIG = useMemo<RightPanelConfig>(
    () => ({
      title: t('reviewerCounts.dailyHourlyTitle'),
      subTitle: `Data as of ${dataAsOf} UTC`,
      showUploadIcon: true,
      showSaveIcon: true,
      showColumnsIcon: true,
      showActionButton: true,
      onUploadClick: handleExportClick,
    }),
    [dataAsOf, handleExportClick]
  );

  const handleColumnToggleAction = useCallback(
    (columnId: string) => {
      const col = (reviewersColumns ?? []).find(
        (c: any) => c?.field === columnId
      );
      if (!col || typeof col.index !== 'number') return;

      setHiddenColumnIndexes((prev) => {
        const set = new Set(prev);
        if (set.has(col.index)) set.delete(col.index - 1);
        else set.add(col.index - 1);
        return Array.from(set);
      });
    },
    [reviewersColumns]
  );

  const handleApplyAction = useCallback(
    async (actionKey: string, values: Record<string, any>) => {
      if (actionKey === 'applyChanges') {
        setApiLoading(true);
        try {
          setSavedFilterValues(values);
          setSavedUiFilterValues({
            ...values,
            selectedReviewers: buildReviewerLabelMap(values?.selectedReviewers),
          });
          const payload = buildPayload(values);
          const reviewCountResult = await runReviewerCount(payload);
          if (
            reviewCountResult &&
            reviewCountResult.reportID &&
            reviewCountResult.reportFilePath &&
            reviewCountResult.reportName
          ) {
            setWaitModalInfo({
              notificationID: reviewCountResult.notificationID,
              reportID: reviewCountResult.reportID,
              reportFilePath: reviewCountResult.reportFilePath,
              reportName: reviewCountResult.reportName,
            });
          }
        } catch {
          console.error('Error occurred while applying changes');
        } finally {
          setApiLoading(false);
        }
      } else if (actionKey === 'reset') {
        setGridData(null);
        setPageNumber(1);
        setReviewCountViewData(undefined);
        setReviewersData([]);
        setReviewersColumns([]);
        setSavedFilterValues({});
        setSavedUiFilterValues({});
        setHiddenColumnIndexes([]);
        setResetCounter((prev) => prev + 1);
        // Reset returns the left panel to Daily ('1'); keep this state in sync so
        // re-selecting Hourly is detected as a change
        setDailyOrHourly('1');
      }
    },
    [dynamicData, reviewersColumns, buildReviewerLabelMap]
  );

  const saveReportConfigPayload = useMemo(() => {
    if (!gridData) return undefined;
    const hasApplied =
      savedFilterValues && Object.keys(savedFilterValues).length > 0;
    if (!hasApplied) return undefined;
    const criteria = buildPayload(savedFilterValues);
    return {
      reportId: reportIdFromUrl ?? 0,
      reportTypeCode: 'RCR003',
      ...criteria,
      selectedCustomColumns: hiddenColumnIndexes,
    };
  }, [gridData, savedFilterValues, reportIdFromUrl, hiddenColumnIndexes]);

  const buildStaticColumns = () => {
    const showAssignmentCols = savedFilterValues?.assignmentGroups?.length > 0;
    const columns = [
      {
        field: 'reviewerName',
        headerName: t('reviewerCounts.columns.reviewer'),
        flex: 1,
        minWidth: 180,
        isVisible: true,
        headerClassName: 'header-class',
        cellClassName: 'cell-class',
      },
    ];
    if (showAssignmentCols) {
      columns.push(
        {
          field: 'assignmentName',
          headerName: t('reviewerCounts.columns.assignedName'),
          flex: 1,
          minWidth: 160,
          isVisible: true,
        } as any,
        {
          field: 'assignmentPath',
          headerName: t('reviewerCounts.columns.assignmentPath'),
          flex: 1,
          minWidth: 180,
          isVisible: true,
        } as any
      );
    }
    columns.push({
      field: 'assignedDocs',
      headerName: t('reviewerCounts.columns.distributedDocs'),
      flex: 1,
      minWidth: 140,
      isVisible: true,
    } as any);
    const finalColumns = columns.map((col, idx) => ({...col, index: idx + 1}));
    return finalColumns;
  };

  const buildColumns = useCallback(
    (tableHeaders: any[], isShowDailyReport: boolean) => {
      const staticColumns = buildStaticColumns();
      const dynamicColumns = (tableHeaders ?? []).map(
        (header: any, idx: number) => {
          let headerName = header.docCountHeading;
          const day = dayjs.utc(header.docCountHeading);
          if (isShowDailyReport) {
            headerName = day.format('YYYY/MM/DD');
          } else {
            const date = day.format('YYYY/MM/DD');
            const startTime = day.add(header.hours, 'hours');
            const endTime = startTime.add(1, 'hours').subtract(1, 'minute');
            headerName = `${date} ${startTime.format('HH:mm')} - ${endTime.format(
              'HH:mm'
            )}`;
          }
          return {
            field: `date_${idx}`,
            headerName: headerName,
            flex: 1,
            minWidth: isShowDailyReport ? 150 : 225,
            isVisible: true,
            index: staticColumns.length + idx + 1,
            headerClassName: header.isWeekend ? 'weekend-header-cell' : '',
            cellClassName: header.isWeekend ? 'weekend-cell' : '',
          };
        }
      );
      const endColumns = [
        {
          field: 'totalReviewedDocs',
          headerName: t('reviewerCounts.columns.totalReviewedDocs'),
          flex: 1,
          minWidth: 140,
          isVisible: true,
          index: staticColumns.length + dynamicColumns.length + 1,
        },
        {
          field: 'reviewedAvg',
          headerName:
            dailyOrHourly === '1'
              ? t('reviewerCounts.columns.avgperday')
              : t('reviewerCounts.columns.avgperhour'),
          flex: 1,
          minWidth: 140,
          isVisible: true,
          index: staticColumns.length + dynamicColumns.length + 2,
        },
      ];
      return [...staticColumns, ...dynamicColumns, ...endColumns];
    },
    [savedFilterValues, dailyOrHourly]
  );

  const handlePageNumber = useCallback((pageNumber: number) => {
    setPageNumber(pageNumber);
  }, []);

  const handleItemsPerPage = useCallback((itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setPageNumber(1);
  }, []);

  const mapReviewerRows = useCallback(
    (lstReviewerAssignmentSummary: any[], tableHeaders: any[]) => {
      if (
        (!lstReviewerAssignmentSummary ||
          lstReviewerAssignmentSummary.length === 0) &&
        (!tableHeaders || tableHeaders.length === 0)
      ) {
        return [
          {
            reviewerName: '',
            assignmentName: '',
            assignmentPath: '',
            assignedDocs: '',
            totalReviewedDocs: '',
            reviewedAvg: '',
          },
        ];
      }
      return (lstReviewerAssignmentSummary ?? []).map((item) => {
        const row: any = {
          reviewerName: item.reviewerName,
          assignmentName: item.assignmentName,
          assignmentPath: item.assignmentPath,
          assignedDocs: item.assignedDocs,
          totalReviewedDocs: item.totalReviewedDocs,
          reviewedAvg: item.reviewedAvg,
        };
        (tableHeaders ?? []).forEach((header: any, idx: number) => {
          row[`date_${idx}`] = item.selectedRangeDetails?.[idx]?.docCount ?? 0;
        });
        return row;
      });
    },
    []
  );

  useEffect(() => {
    if (!reviewCountViewData) return;
    const apiResponse = reviewCountViewData;
    if (apiResponse && apiResponse.reviewerCountReportResponse?.length > 0) {
      const report = apiResponse.reviewerCountReportResponse[0];
      const tableHeaders = report?.tableHeaders ?? [];
      const isShowDailyReport = report.isShowDailyReport;
      const lstReviewerAssignmentSummary: ReviewerAssignmentSummary[] =
        report?.lstReviewerAssignmentSummary ?? [];
      const revColumns = buildColumns(tableHeaders, isShowDailyReport);
      setReviewersColumns(revColumns);
      const revData = mapReviewerRows(
        lstReviewerAssignmentSummary,
        tableHeaders
      );
      setReviewersData(revData);
    } else {
      setReviewersColumns([]);
      setReviewersData([]);
    }
  }, [reviewCountViewData]);

  const mapRows = () => reviewersData ?? [];

  const aggregateSummary = useCallback(() => {
    return gridData?.reviewerCountReportResponse[0]?.reviewerSummary;
  }, [gridData]);

  const summary = aggregateSummary();

  // Read via ref so this callback stays stable; a new identity makes FilterSection
  // re-emit its pre-reset values and flip the report back to Hourly after Reset
  const dailyOrHourlyRef = useRef(dailyOrHourly);
  dailyOrHourlyRef.current = dailyOrHourly;

  const updateDailyOrHourly = useCallback((value: any) => {
    if (
      value.selectedDailyHourly &&
      value.selectedDailyHourly !== dailyOrHourlyRef.current
    ) {
      dailyOrHourlyRef.current = value.selectedDailyHourly;
      setDailyOrHourly(value.selectedDailyHourly);
      setSavedUiFilterValues(value);
      if (value.selectedDailyHourly === '1') {
        setResetDatePicker((prev: any) => prev + 1);
      } else {
        setIsDailySwitched(true);
        setTimeout(() => {
          setIsDailySwitched(false);
        }, 2000);
      }
    }
  }, []);

  const mergeConfig = useMemo(() => {
    if (!adaptedConfig || !dynamicData || !initialValuesFromLeftPanel) {
      return null;
    }
    return mergeApiDataIntoConfigForReviewersCount(
      adaptedConfig,
      dynamicData,
      initialValuesForMerge,
      isDailySwitched
    );
  }, [adaptedConfig, dynamicData, initialValuesForMerge, isDailySwitched]);

  const formatDate = (input: any, dateHourFlag: boolean) => {
    const date = new Date(input);
    const formattedDate =
      date.getFullYear() +
      '/' +
      String(date.getMonth() + 1).padStart(2, '0') +
      '/' +
      String(date.getDate()).padStart(2, '0');
    const time =
      String(date.getHours()).padStart(2, '0') +
      ':' +
      String(date.getMinutes()).padStart(2, '0') +
      ':' +
      String(date.getSeconds()).padStart(2, '0');
    if (dateHourFlag) {
      return formattedDate;
    } else {
      return `${formattedDate} ${time}`;
    }
  };

  useEffect(() => {
    if (
      !reportIdFromUrl ||
      Array.isArray(leftPanelResolvedReport) ||
      reportLabel ||
      loading
    )
      return;
    (async () => {
      try {
        setIsFromBGtask(true);
        setIsLoadingReportState(true);
        const response = await getReportState(reportIdFromUrl);
        if (response && response.reviewerCountReportResponse?.length > 0) {
          const reportItem = response.reviewerCountReportResponse[0];
          const showDailyReport =
            leftPanelResolvedReport.showDailyReport ??
            reportItem?.isShowDailyReport;
          const isDailyOrHourly = resolveSelectedDailyHourly({
            showDailyReport,
            reportID: reportIdFromUrl,
          });
          const values = {
            selectedReviewers: leftPanelResolvedReport.selectedReviewers,
            assignmentGroups: leftPanelResolvedReport.selectedAssignments,
            fromDate: formatDate(
              leftPanelResolvedReport.fromDateTime,
              isDailyOrHourly === '1'
            ),
            toDate: formatDate(
              leftPanelResolvedReport.toDateTime,
              isDailyOrHourly === '1'
            ),
            selectedDailyHourly: isDailyOrHourly,
            showDailyReport: isDailyOrHourly === '1',
          };
          setDailyOrHourly(isDailyOrHourly);
          setSavedFilterValues(values);
          setSavedUiFilterValues({
            ...values,
            selectedReviewers: buildReviewerLabelMap(values?.selectedReviewers),
          });
          if (response.reviewerCountReportResponse[0].tableHeaders.length > 0) {
            setReviewCountViewData(response);
            setGridData(response);
          } else {
            setReviewCountViewData(undefined);
            setReviewersColumns([]);
            setReviewersData([]);
            setGridData({
              reviewerCountReportResponse: [],
              noDataMessage: t('common.messageForNoGridData'),
            } as any);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsFromBGtask(false);
        setIsLoadingReportState(false);
      }
    })();
  }, [
    reportIdFromUrl,
    leftPanelResolvedReport,
    reportLabel,
    loading,
    buildReviewerLabelMap,
  ]);

  const handleViewResult = (result: any) => {
    if (!result) return;
    const reviewerApiData =
      result?.reviewerCountReportResponse[0]?.lstReviewerAssignmentSummary;
    if (reviewerApiData.length > 0) {
      setReviewCountViewData(result);
      setGridData(result);
    } else {
      setReviewCountViewData(undefined);
      setReviewersColumns([]);
      setReviewersData([]);
      setGridData({
        totalDisplayRecords: 0,
        reviewerCountReportResponse: [],
        noDataMessage: t('common.messageForNoGridData'),
      } as any);
    }
    setWaitModalInfo(null);
  };

  useEffect(() => {
    const curr = reviewersColumns?.length ?? 0;
    const prev = prevColsLenRef.current;
    if (prev === 0 && curr > 0) {
      setSavedDataTick((t) => t + 1);
      setRightPanelColumns([]);
      setTimeout(() => setRightPanelColumns(reviewersColumns), 0);
    } else {
      setRightPanelColumns(reviewersColumns);
    }
    prevColsLenRef.current = curr;
  }, [reviewersColumns]);

  const savedDataWithColumnsVersion = useMemo(() => {
    const base = (leftPanelData as any) ?? {};
    return {...base, __tick: savedDataTick};
  }, [leftPanelData, savedDataTick]);

  const selectedFilterValues = useMemo(() => {
    if (!reportIdFromUrl || Object.keys(savedFilterValues).length === 0) {
      return undefined;
    }

    const reviewerList = Array.isArray((leftPanelData as any)?.reviewerList)
      ? (leftPanelData as any).reviewerList
      : [];

    return {
      ...savedFilterValues,
      selectedReviewers: toSelectedReviewersBooleanArray(
        savedFilterValues.selectedReviewers,
        reviewerList
      ),
    };
  }, [reportIdFromUrl, savedFilterValues, leftPanelData]);

  return (
    <>
      {loading ||
      !reportConfig ||
      isLoadingReportState ||
      (reportIdFromUrl && isLoadingLeftPanelReport) ? (
        <div
          data-testid="loading-container"
          style={{display: 'flex', justifyContent: 'center', marginTop: '50px'}}
        >
          <ArrLoadingSpinner show={true} overlay={true} isOpen={true} />
        </div>
      ) : (
        <>
          <StandardReports
            className="reviewer-count-container"
            title={
              location?.state?.fromReportLanding
                ? (location?.state?.item?.label ?? '')
                : (reportConfig?.title ?? '')
            }
            showInfoIcon={reportConfig.showInfoIcon}
            infoContent={reportConfig?.infoContent}
            reportsConfig={mergeConfig}
            onFilterChange={updateDailyOrHourly}
            loading={isLoadingLeftPanel || loading || apiLoading}
            onAction={handleApplyAction}
            customHeaderContent={
              reviewersData && reviewersData?.length > 0
                ? summary && docCountSummaryView
                : undefined
            }
            data={gridData}
            columns={rightPanelColumns}
            mapRows={mapRows}
            resetDatePicker={resetDatePicker}
            rightPanelHeaderConfig={RIGHT_PANEL_HEADER_CONFIG}
            selectedOption={null}
            isNoData={gridData === null}
            getRowId={(row: any) => row?.reviewerName + row?.assignmentName}
            selectedFilterValues={selectedFilterValues}
            savedData={savedDataWithColumnsVersion}
            saveReportConfig={saveReportConfigPayload}
            onColumnToggleAction={handleColumnToggleAction}
            gridConfig={{
              primaryKey: 'reviewerName',
              totalRecords: reviewersData?.length ?? 0,
              currentPage: pageNumber,
              disableVirtualization: false,
              pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
              itemsPerPage,
              paginationMode: 'client',
              sortingMode: 'client',
              hideFooter: (reviewersData?.length ?? 0) <= PAGE_SIZE,
              getRowHeight: () => 'auto',
              setItemsPerPage: handleItemsPerPage,
              onPageChange: handlePageNumber,
              entityType: 'entries',
            }}
          />
          {waitModalInfo &&
            typeof waitModalInfo.notificationID === 'number' &&
            typeof waitModalInfo.reportID === 'number' &&
            waitModalInfo.notificationID > 0 &&
            waitModalInfo.reportID > 0 && (
              <ArrWaitToCompleteModalWrapper
                key={`${waitModalInfo.notificationID}-${waitModalInfo.reportID}`}
                showClose={false}
                notificationId={waitModalInfo.notificationID}
                reportId={waitModalInfo.reportID}
                onComplete={async (reportId: number) => {
                  const reviewCountViewResult = await runReviewerCountView({
                    reportID: reportId,
                    reportFilePath: waitModalInfo.reportFilePath,
                    reportName: waitModalInfo.reportName,
                  });
                  handleViewResult(reviewCountViewResult);
                }}
              />
            )}
          {toastMsgConfig.show && (
            <ArrToastNotification
              onClose={() => setToastMsgConfig({show: false} as any)}
              status={[toastMsgConfig.type ?? ToastType.Success]}
              message={toastMsgConfig.message ?? ''}
              title={toastMsgConfig.title}
            />
          )}
        </>
      )}
    </>
  );
};

export default ReviewersCountContainer;
